import { applyAIFilters, parseAIResponse } from '../utils/filterValidation'
import { BuildingSearchFilters } from '../types'

export interface AIFilterResult {
  success: boolean
  filters: BuildingSearchFilters
  errors: string[]
  appliedFilters: string[]
  originalResponse?: string | object
}

export interface AIFilterOptions {
  mergeWithExisting?: boolean
  validateOnly?: boolean
}

/**
 * Main service function to apply building filters from AI assistant response
 */
export const applyBuildingFiltersFromAI = (
  aiResponse: string | object,
  existingFilters: BuildingSearchFilters = {},
  options: AIFilterOptions = {}
): AIFilterResult => {
  const { mergeWithExisting = true } = options

  try {
    // Apply AI filters with sanitization and validation
    const result = applyAIFilters(
      aiResponse,
      mergeWithExisting ? existingFilters : {}
    )

    return {
      success: result.errors.length === 0,
      filters: result.filters,
      errors: result.errors,
      appliedFilters: result.appliedFilters,
      originalResponse: aiResponse,
    }
  } catch (error) {
    return {
      success: false,
      filters: existingFilters,
      errors: [
        `Critical error processing AI response: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      ],
      appliedFilters: [],
      originalResponse: aiResponse,
    }
  }
}

/**
 * Validate AI response without applying filters
 */
export const validateAIResponse = (
  aiResponse: string | object
): AIFilterResult => {
  return applyBuildingFiltersFromAI(aiResponse, {}, { validateOnly: true })
}

/**
 * Parse AI response and extract filter structure without validation
 */
export const extractFiltersFromAI = (
  aiResponse: string | object
): Record<string, any> => {
  try {
    return parseAIResponse(aiResponse)
  } catch (error) {
    console.warn('Failed to extract filters from AI response:', error)
    return {}
  }
}

/**
 * Get user-friendly descriptions of applied filters
 */
export const getFilterDescriptions = (appliedFilters: string[]): string[] => {
  const descriptions: Record<string, string> = {
    category: 'Building category',
    type: 'Building type',
    area: 'Floor area range',
    building_year: 'Construction year range',
    floor_nb: 'Number of floors',
    unit_nb: 'Number of units',
    height: 'Building height',
    classification: 'Building classification',
    heating_type: 'Heating system',
    energy_efficiency: 'Energy efficiency class',
    location_search: 'Location search',
    purchase_price: 'Purchase price range',
    rent_price: 'Rent price range',
    transaction_date: 'Transaction date range',
    new_construction: 'New construction filter',
    is_furnished: 'Furnished filter',
    pets_allowed: 'Pet-friendly filter',
    wheelchair_accessible: 'Accessibility filter',
    geo_bounding_box: 'Geographic area filter',
    solar_potential: 'Solar potential rating',
    view_quality: 'View quality rating',
    noise_level: 'Noise level filter',
    transport_accessibility: 'Transport accessibility',
    minergie: 'Minergie certification',
    lake_view: 'Lake view filter',
    mountain_view: 'Mountain view filter',
  }

  return appliedFilters.map(
    (filter) => descriptions[filter] || filter.replace(/_/g, ' ')
  )
}

/**
 * Create summary text for applied filters
 */
export const createFilterSummary = (result: AIFilterResult): string => {
  if (!result.success) {
    return 'Failed to apply AI filters'
  }

  if (result.appliedFilters.length === 0) {
    return 'No valid filters found in AI response'
  }

  const descriptions = getFilterDescriptions(result.appliedFilters)

  if (descriptions.length === 1) {
    return `Applied filter: ${descriptions[0]}`
  } else if (descriptions.length <= 3) {
    return `Applied filters: ${descriptions.join(', ')}`
  } else {
    return `Applied ${descriptions.length} filters: ${descriptions.slice(0, 2).join(', ')} and ${descriptions.length - 2} more`
  }
}
